import { type FC, type PropsWithChildren, useEffect, useState } from 'react'
import { Dropdown, Form } from 'react-bootstrap'
import classNames from 'classnames'
import { parsePhoneNumber } from 'react-phone-number-input'
import { usePrevious } from 'react-use'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { Button } from '../../../../components/Button/Button'
import {
  type AccountSchedulingSettings,
  NotificationChannel,
  type ReminderSettings,
  ReminderType,
  type SmsAreaCode,
  type SmsNotificationSettings,
  type SmsNumber,
  SmsNumberStatus,
  SmsTemplatePurpose,
} from '../../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  useCancelSmsNumberSetupMutation,
  useGetReminderSettingsQuery,
  useGetSmsAreaCodesQuery,
  useGetSmsNumbersQuery,
  useRequestSmsNumberMutation,
} from '../../../../store/scheduling/scheduling'
import { isFetchBaseQueryError } from '../../../../models/ApiErrors'
import { addToast } from '../../../../store/toast/toastSlice'
import { BookingActionMenu } from '../../../Scheduling/rows/BookingActionMenu'
import { ConfirmDialog } from '../../../Scheduling/shared/ConfirmDialog'
import { type UpdateAccountSettings } from '../SchedulingSetupTab'
import { areaCodeLocation } from './areaCodeLocation'
import { ChangeSmsNumberModal } from './ChangeSmsNumberModal'
import { SettingsCard, SettingsRow, SettingsSection } from './SettingsSection'
import { SmsTextPreview } from './SmsTextPreview'

interface TextMessagesSectionProps {
  settings: AccountSchedulingSettings
  readOnly: boolean
  isSaving?: boolean
  showReminderStatus: boolean
  showTextPreviews?: boolean
  onUpdate: UpdateAccountSettings
  onShowReminders: () => void
  onShowMessageTemplates: () => void
}

const ENTERPRISE_MANAGED_MESSAGE = 'Sending text messages to clients is managed at the enterprise level.'

const formatSendingNumber = (phone: string): string => {
  const parsed = parsePhoneNumber(phone)
  return parsed != null ? `+${parsed.countryCallingCode} ${parsed.formatNational()}` : phone
}

interface TextsSwitchRowProps {
  label: string
  description: string
  checked: boolean
  disabled: boolean
  alignTop?: boolean
  onChange: (checked: boolean) => void
}

const TextsSwitchRow: FC<PropsWithChildren<TextsSwitchRowProps>> = ({ label, description, checked, disabled, alignTop = false, onChange, children }) => (
  <div className={classNames('d-flex gap-3', alignTop ? 'align-items-start' : 'align-items-center')}>
    <Form.Switch className='mb-0' checked={checked} disabled={disabled} aria-label={label} onChange={(event) => onChange(event.currentTarget.checked)} />
    <div className='d-flex flex-column'>
      <span className='bold'>{label}</span>
      <span className='paragraph-small zocks-text-gray-600'>{description}</span>
      {children}
    </div>
  </div>
)

const SubSwitchRow: FC<PropsWithChildren<TextsSwitchRowProps>> = (props) => (
  <div className={classNames('d-flex gap-2 pt-3 border-top', props.alignTop ? 'align-items-start' : 'align-items-center')}>
    <i aria-hidden='true' className='bi bi-arrow-return-right ms-2 zocks-text-gray-400' />
    <TextsSwitchRow {...props} />
  </div>
)

interface SectionLinkProps {
  label: string
  disabled?: boolean
  onClick: () => void
}

const SectionLink: FC<SectionLinkProps> = ({ label, disabled = false, onClick }) =>
  disabled ? (
    <>{label}</>
  ) : (
    <Button className='d-inline p-0 align-baseline zocks-text-primary text-decoration-underline' onClick={onClick}>
      {label}
    </Button>
  )

const textReminderCount = (meetingReminders: ReminderSettings): number =>
  meetingReminders.entries.filter((entry) => entry.channels.includes(NotificationChannel.SMS)).length

interface ReminderTextsStatusProps {
  meetingReminders: ReminderSettings
  disabled: boolean
  onShowReminders: () => void
}

const ReminderTextsStatus: FC<ReminderTextsStatusProps> = ({ meetingReminders, disabled, onShowReminders }) => {
  const textCount = textReminderCount(meetingReminders)

  if (!meetingReminders.enabled || textCount === 0) {
    return (
      <span className='d-flex align-items-center gap-2 paragraph-small zocks-text-gray-900'>
        <i aria-hidden='true' className='bi bi-exclamation-circle flex-shrink-0' />
        <span>
          No text reminders are set up at the moment. Set them up in <SectionLink label='Reminders' disabled={disabled} onClick={onShowReminders} />.
        </span>
      </span>
    )
  }

  return (
    <span className='d-flex align-items-center gap-2 paragraph-small zocks-text-gray-900'>
      <i aria-hidden='true' className='bi bi-check-circle flex-shrink-0' />
      <span>
        {textCount} of {meetingReminders.entries.length} reminders go out by text.
        {disabled ? null : (
          <>
            {' '}
            <SectionLink label='Edit reminders' disabled={disabled} onClick={onShowReminders} />
          </>
        )}
      </span>
    </span>
  )
}

const HelperNote: FC<PropsWithChildren> = ({ children }) => (
  <div className='d-flex align-items-start gap-2 paragraph-small zocks-text-gray-700'>
    <i aria-hidden='true' className='bi bi-info-circle flex-shrink-0' />
    <span>{children}</span>
  </div>
)

type TurnOffConfirm = { kind: 'main' } | { kind: 'last-sub-switch'; subSwitch: SubSwitchKey }

type SubSwitchKey = 'reminders' | 'cancellations'

const TURN_OFF_BODY = "Clients won't get texts about their meetings, and the booking page stops asking for a mobile number. Emails still go out as usual."

const LAST_SUB_SWITCH_TURN_OFF_PREFIX = 'Meeting reminders and cancellations are both off, so text messages turn off too.'

const textRemindersTurnOffBody = (count: number) =>
  `Clients won't get texts about their meetings, and the booking page stops asking for a mobile number. ${
    count === 1 ? '1 meeting reminder is' : `${count} meeting reminders are`
  } set to text. Reminders set to text only won't go out until you change them or turn texts back on.`

const PENDING_NUMBER_POLLING_INTERVAL = 15000

const isPendingNumber = (number: SmsNumber) => number.status === SmsNumberStatus.PENDING

const isSendingNumber = (number: SmsNumber) => number.isDefault && number.status === SmsNumberStatus.ACTIVE

const numberLocation = (number: SmsNumber, areaCodes: SmsAreaCode[] | undefined): string | null => {
  const areaCode = areaCodes?.find((candidate) => candidate.areaCode === number.areaCode)
  return areaCode != null ? areaCodeLocation(areaCode) : null
}

interface SmsNumberRowProps {
  number: SmsNumber
  areaCodes: SmsAreaCode[] | undefined
  detail?: string
  onCancelSetup?: () => void
}

const SmsNumberRow: FC<SmsNumberRowProps> = ({ number, areaCodes, detail, onCancelSetup }) => {
  const description = [numberLocation(number, areaCodes), detail].filter((part) => part != null).join(' · ')
  const formattedPhone = formatSendingNumber(number.phone)

  return (
    <div className='d-flex align-items-center justify-content-between gap-3 p-3 rounded-2 zocks-bg-color-gray-100'>
      <div className='d-flex flex-column'>
        <span>{formattedPhone}</span>
        {description !== '' ? <span className='paragraph-small zocks-text-gray-700'>{description}</span> : null}
      </div>
      {isPendingNumber(number) ? (
        <div className='d-flex align-items-center gap-2 flex-shrink-0'>
          <span className='rounded-pill px-2 py-1 paragraph-small zocks-bg-color-warning zocks-text-gray-900'>Setting up</span>
          {onCancelSetup != null ? (
            <BookingActionMenu ariaLabel={`${formattedPhone} actions`}>
              <Dropdown.Item className='text-danger' onClick={onCancelSetup}>
                Cancel setup
              </Dropdown.Item>
            </BookingActionMenu>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

const SendingNumberMessage: FC<PropsWithChildren> = ({ children }) => (
  <SettingsRow>
    <span className='paragraph-small zocks-text-gray-600'>{children}</span>
  </SettingsRow>
)

interface SendingNumbersProps {
  numbers: SmsNumber[] | undefined
  areaCodes: SmsAreaCode[] | undefined
  isLoading: boolean
  isError: boolean
  disabled: boolean
  onChangeNumber: () => void
  onCancelSetup: (number: SmsNumber) => void
}

const SendingNumbers: FC<SendingNumbersProps> = ({ numbers, areaCodes, isLoading, isError, disabled, onChangeNumber, onCancelSetup }) => {
  if (isLoading) {
    return (
      <SettingsRow>
        <Spinner size='sm' />
      </SettingsRow>
    )
  }

  if (isError || numbers == null) {
    return <SendingNumberMessage>Couldn&apos;t load the sending number.</SendingNumberMessage>
  }

  const sendingNumber = numbers.find(isSendingNumber)
  const pendingNumber = numbers.find(isPendingNumber)

  if (pendingNumber != null) {
    return (
      <SettingsRow className='d-flex flex-column gap-3'>
        <span className='paragraph-small zocks-text-gray-700'>Clients get texts from your current number until the new one is ready.</span>
        {sendingNumber != null ? <SmsNumberRow number={sendingNumber} areaCodes={areaCodes} detail='Current number' /> : null}
        <SmsNumberRow
          number={pendingNumber}
          areaCodes={areaCodes}
          detail="Replaces your current number when it's ready."
          onCancelSetup={disabled ? undefined : () => onCancelSetup(pendingNumber)}
        />
      </SettingsRow>
    )
  }

  if (sendingNumber == null) {
    return <SendingNumberMessage>No sending number is set up yet.</SendingNumberMessage>
  }

  return (
    <SettingsRow className='d-flex flex-column gap-3'>
      <span className='paragraph-small zocks-text-gray-700'>Clients get texts from this number.</span>
      <SmsNumberRow number={sendingNumber} areaCodes={areaCodes} />
      {disabled ? null : (
        <div className='d-flex align-items-center justify-content-between gap-3'>
          <span className='paragraph-small zocks-text-gray-700'>Use an area code clients recognize, like your office&apos;s.</span>
          <Button variant='grayButton' className='flex-shrink-0' onClick={onChangeNumber}>
            Change number
          </Button>
        </div>
      )}
    </SettingsRow>
  )
}

interface SendingNumberCardProps {
  showTextPreviews: boolean
  disabled: boolean
  onShowMessageTemplates: () => void
}

const SendingNumberCard: FC<SendingNumberCardProps> = ({ showTextPreviews, disabled, onShowMessageTemplates }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const dispatch = useAppDispatch()
  const [isChangeOpen, setIsChangeOpen] = useState(false)
  const [isWaitingForNumber, setIsWaitingForNumber] = useState(false)
  const [numberToCancel, setNumberToCancel] = useState<SmsNumber | null>(null)
  const {
    data: numbers,
    isLoading,
    isError,
  } = useGetSmsNumbersQuery({ accountId }, { pollingInterval: isWaitingForNumber ? PENDING_NUMBER_POLLING_INTERVAL : 0, skipPollingIfUnfocused: true })
  const [requestSmsNumber, { isLoading: isRequesting, error: requestError, originalArgs, reset }] = useRequestSmsNumberMutation()
  const [cancelSmsNumberSetup, { isLoading: isCanceling }] = useCancelSmsNumberSetupMutation()
  const { data: areaCodes, isFetching: isFetchingAreaCodes, isError: isAreaCodesError, refetch: refetchAreaCodes } = useGetSmsAreaCodesQuery({ accountId })
  const sendingNumber = numbers?.find(isSendingNumber)
  const currentNumberLabel = sendingNumber != null ? formatSendingNumber(sendingNumber.phone) : 'your current number'
  const pendingNumberId = numbers?.find(isPendingNumber)?.id ?? null
  const previousPendingNumberId = usePrevious(pendingNumberId)
  const hasPendingNumber = pendingNumberId != null
  if (hasPendingNumber !== isWaitingForNumber) {
    setIsWaitingForNumber(hasPendingNumber)
  }
  const isNumberToCancelPending = numberToCancel != null && numberToCancel.id === pendingNumberId
  if (numberToCancel != null && !isCanceling && !isNumberToCancelPending) {
    setNumberToCancel(null)
  }

  useEffect(() => {
    if (
      previousPendingNumberId != null &&
      numbers?.some((number) => number.id === previousPendingNumberId && number.status === SmsNumberStatus.ACTIVE) === true
    ) {
      dispatch(addToast({ variant: 'success', title: 'Sending number changed' }))
    }
  }, [dispatch, numbers, previousPendingNumberId])

  const handleCloseChange = () => {
    setIsChangeOpen(false)
    reset()
  }

  const handleRequest = async (areaCode: string) => {
    const result = await requestSmsNumber({ accountId, areaCode })
    if (!('error' in result)) {
      handleCloseChange()
      dispatch(addToast({ variant: 'success', title: result.data.status === SmsNumberStatus.ACTIVE ? 'Sending number changed' : 'Setting up new number' }))
    }
  }

  const handleCancelSetup = async (number: SmsNumber) => {
    const result = await cancelSmsNumberSetup({ accountId, numberId: number.id })
    setNumberToCancel(null)
    if ('error' in result) {
      const isSetupOver = isFetchBaseQueryError(result.error) && (result.error.status === 404 || result.error.status === 409)
      if (!isSetupOver) {
        dispatch(addToast({ variant: 'error', title: "Couldn't cancel the number setup", messages: ['Please try again.'] }))
      }
    } else {
      dispatch(addToast({ variant: 'success', title: 'Number setup canceled' }))
    }
  }

  return (
    <div className='d-flex flex-column gap-2'>
      <SettingsCard>
        <SendingNumbers
          numbers={numbers}
          areaCodes={areaCodes}
          isLoading={isLoading}
          isError={isError}
          disabled={disabled}
          onChangeNumber={() => setIsChangeOpen(true)}
          onCancelSetup={setNumberToCancel}
        />
      </SettingsCard>
      <HelperNote>
        Other businesses on Zocks can text from this number too. Every text names your company and the advisor, so clients know who it&apos;s from.{' '}
        {showTextPreviews ? null : (
          <>
            See what&apos;s sent in <SectionLink label='Message templates' onClick={onShowMessageTemplates} />.{' '}
          </>
        )}
        Clients can reply STOP to stop all texts sent through Zocks, even after you change numbers. Other replies are ignored.
      </HelperNote>
      {isChangeOpen ? (
        <ChangeSmsNumberModal
          areaCodes={areaCodes}
          isLoadingAreaCodes={isFetchingAreaCodes}
          isAreaCodesError={isAreaCodesError && !isFetchingAreaCodes}
          isSaving={isRequesting}
          error={requestError}
          requestedAreaCode={originalArgs?.areaCode ?? null}
          currentAreaCode={sendingNumber?.areaCode ?? null}
          onRequest={(areaCode) => void handleRequest(areaCode)}
          onRetryAreaCodes={() => void refetchAreaCodes()}
          onCancel={handleCloseChange}
        />
      ) : null}
      {numberToCancel != null ? (
        <ConfirmDialog
          title='Cancel number setup?'
          body={`${formatSendingNumber(numberToCancel.phone)} won't replace your current number. Texts keep going out from ${currentNumberLabel}.`}
          confirmLabel='Cancel setup'
          confirmVariant='primary'
          cancelLabel='Keep setting up'
          centered
          isLoading={isCanceling}
          onConfirm={() => void handleCancelSetup(numberToCancel)}
          onCancel={() => setNumberToCancel(null)}
        />
      ) : null}
    </div>
  )
}

export const TextMessagesSection: FC<TextMessagesSectionProps> = ({
  settings,
  readOnly,
  isSaving = false,
  showReminderStatus,
  showTextPreviews = false,
  onUpdate,
  onShowReminders,
  onShowMessageTemplates,
}) => {
  const [turnOffConfirm, setTurnOffConfirm] = useState<TurnOffConfirm | null>(null)
  const { notifications } = settings
  const { sms } = notifications
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: reminderSettings } = useGetReminderSettingsQuery({ accountId }, { skip: !showReminderStatus })
  const meetingReminders = reminderSettings?.data.find((reminders) => reminders.reminderType === ReminderType.MEETING)
  const smsReminderCount = meetingReminders != null ? textReminderCount(meetingReminders) : 0
  const mainTurnOffBody = smsReminderCount > 0 ? textRemindersTurnOffBody(smsReminderCount) : TURN_OFF_BODY
  const smsEnabled = sms.enabled
  const enterpriseEnforced = sms.enterpriseEnforced === true

  const updateSms = (change: Partial<SmsNotificationSettings>) => onUpdate({ notifications: { ...notifications, sms: { ...sms, ...change } } })

  const handleToggle = (enabled: boolean) => {
    if (enabled) {
      updateSms({ enabled: true })
    } else {
      setTurnOffConfirm({ kind: 'main' })
    }
  }

  const handleSubSwitchToggle = (subSwitch: SubSwitchKey, checked: boolean) => {
    const otherSubSwitchOn = subSwitch === 'reminders' ? sms.cancellations : sms.reminders
    if (!checked && !otherSubSwitchOn && !enterpriseEnforced) {
      setTurnOffConfirm({ kind: 'last-sub-switch', subSwitch })
    } else {
      updateSms({ [subSwitch]: checked })
    }
  }

  const handleConfirmTurnOff = () => {
    if (turnOffConfirm?.kind === 'last-sub-switch') {
      updateSms({ enabled: false, [turnOffConfirm.subSwitch]: false })
    } else {
      updateSms({ enabled: false })
    }
    setTurnOffConfirm(null)
  }

  return (
    <SettingsSection
      title='Text messages'
      subtitle='Send clients texts about their meetings, on top of emails. Clients choose whether to get texts when they book. These settings apply to everyone in the account.'
      isSaving={isSaving}
    >
      <h6 className='mb-0 zocks-text-gray-900'>Texts to clients</h6>
      <SettingsCard>
        <SettingsRow className='d-flex flex-column gap-3'>
          {enterpriseEnforced ? (
            <div className='d-flex align-items-center gap-2 rounded-2 p-3 zocks-bg-color-primary-light paragraph-small'>
              <i className='bi bi-info-circle flex-shrink-0' />
              {ENTERPRISE_MANAGED_MESSAGE}
            </div>
          ) : null}
          <TextsSwitchRow
            label='Send text messages to clients'
            description='Only clients who agree to texts when they book get them.'
            checked={smsEnabled}
            disabled={readOnly || enterpriseEnforced}
            onChange={handleToggle}
          />
          <SubSwitchRow
            label='Meeting reminders'
            description='Send reminders by text as well as email. You choose which reminders go out by text.'
            checked={sms.reminders}
            disabled={readOnly || !smsEnabled}
            alignTop={showTextPreviews}
            onChange={(checked) => handleSubSwitchToggle('reminders', checked)}
          >
            {meetingReminders != null && smsEnabled && sms.reminders ? (
              <ReminderTextsStatus meetingReminders={meetingReminders} disabled={readOnly} onShowReminders={onShowReminders} />
            ) : null}
            {showTextPreviews ? <SmsTextPreview purpose={SmsTemplatePurpose.MEETING_REMINDER} /> : null}
          </SubSwitchRow>
          <SubSwitchRow
            label='Cancellations'
            description='Sent when someone on your team cancels a meeting. Messages added when canceling go out by email only.'
            checked={sms.cancellations}
            disabled={readOnly || !smsEnabled}
            alignTop={showTextPreviews}
            onChange={(checked) => handleSubSwitchToggle('cancellations', checked)}
          >
            {showTextPreviews ? <SmsTextPreview purpose={SmsTemplatePurpose.CANCELLATION_NOTICE} /> : null}
          </SubSwitchRow>
        </SettingsRow>
      </SettingsCard>
      {smsEnabled ? (
        <HelperNote>Texts aren&apos;t sent during quiet hours, based on the client&apos;s area code. Clients still get emails as usual.</HelperNote>
      ) : null}
      <h6 className='mb-0 zocks-text-gray-900'>Sending number</h6>
      <SendingNumberCard showTextPreviews={showTextPreviews} disabled={readOnly} onShowMessageTemplates={onShowMessageTemplates} />
      {turnOffConfirm != null ? (
        <ConfirmDialog
          title='Turn off text messages?'
          body={turnOffConfirm.kind === 'last-sub-switch' ? `${LAST_SUB_SWITCH_TURN_OFF_PREFIX} ${mainTurnOffBody}` : mainTurnOffBody}
          confirmLabel='Turn off'
          confirmVariant='primary'
          centered
          onConfirm={handleConfirmTurnOff}
          onCancel={() => setTurnOffConfirm(null)}
        />
      ) : null}
    </SettingsSection>
  )
}
